/**
 * The phone's bar for a full-screen page reached from MY GAMES
 * (DESIGN-CHANGELOG 2026-09-03; `design/GT Mobile Boards.dc.html` — the MY
 * INTERESTS and GOTY blocks). Geometry copied from the round page's own and
 * from `screens/Detail.tsx`'s `DetailTopBar`, so the phone's full-screen pages
 * look like siblings: sticky, `z:20`, a 44px bordered `◂ MY GAMES` control
 * (a thumb target — the desktop's bare text is not one), then a title with a
 * mono sub-line under it, and an optional right-hand control.
 *
 * The header and tab bar are gated off while one of these pages is up
 * (`headerVM().hdrOn`), so this bar carries the only way out.
 */

import type { ReactNode } from 'react';

import { useQd } from '../store/index.ts';

const MONO = 'var(--gt-font-mono)';

export interface PageBarProps {
  title: ReactNode;
  /** the mono line under the title — `N ON THE LIST`, `NOBODY ELSE SEES THIS · …` */
  sub?: ReactNode;
  /** a control in the bar's right slot — MY INTERESTS' BOARD / LIST toggle */
  right?: ReactNode;
}

export function PageBar({ title, sub, right }: PageBarProps): JSX.Element {
  const nav = useQd((s) => s.nav);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 14px',
        borderBottom: '1px solid var(--gt-line)',
        background: 'var(--gt-panel)',
        position: 'sticky',
        top: 0,
        zIndex: 20,
      }}
    >
      <div
        onClick={() => nav('mine')}
        style={{
          display: 'flex',
          gap: 7,
          alignItems: 'center',
          justifyContent: 'center',
          height: 44,
          padding: '0 11px',
          border: '1px solid var(--gt-border-2)',
          color: 'var(--gt-text-4)',
          fontFamily: MONO,
          fontSize: '9.5px',
          letterSpacing: '.05em',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flex: 'none',
          boxSizing: 'border-box',
        }}
      >
        ◂ MY GAMES
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
        <span
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '9.5px',
            lineHeight: 1.6,
            color: 'var(--gt-text)',
            transform: 'translateY(0.13em)',
            whiteSpace: 'nowrap',
            overflowX: 'hidden',
            overflowY: 'visible',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </span>
        {sub ? (
          <span
            style={{
              fontFamily: MONO,
              fontSize: '8.5px',
              letterSpacing: '.06em',
              color: 'var(--gt-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {sub}
          </span>
        ) : null}
      </div>
      {right ? <div style={{ flex: 'none' }}>{right}</div> : null}
    </div>
  );
}

export default PageBar;
